import { guardRouteMutation } from '../routes';
import { logAudit } from '../audit';
import { createMissionsGateway } from '../gateways/missions';
import type { Mission, MissionLead } from './missions-types';

// Re-export types for back-compat with existing consumers that import from
// api/missions directly. New code should prefer importing from
// './missions-types'.
export type { Mission, MissionLead, MissionLeadTier, MissionDashboard } from './missions-types';

function getActorEmailSafe(): string | null {
  try {
    type GlobalWithUser = { __USER__?: { email?: string } };
    const g = globalThis as GlobalWithUser;
    const email = g?.__USER__?.email;
    return typeof email === 'string' ? email : null;
  } catch {
    return null;
  }
}

// ── D-33 R1 / R2: no silent failures, no graceful degradation. ──
// Every function calls the gateway directly; SL errors propagate to
// consumers, which surface them through React Query error state
// (queries) or onError (mutations). The previous module-level
// `memoryMissions` + `memoryMissionLeads` fallback silently returned
// stale/fake data on SL failure — the exact pattern that amplified
// the missions abort flood (Steffen 2026-04-25, missing SL-truth
// signal). Burn-down tracked in memoryServerData guard (PR #1122).

export async function listMissions(
  currentUserEmail?: string,
  role: 'Admin' | 'Sales' | 'Viewer' = 'Viewer',
): Promise<Mission[]> {
  const gw = createMissionsGateway();
  // Admins see all missions; other roles restricted by owner email.
  const emailForFilter = role === 'Admin' ? undefined : currentUserEmail;
  return gw.list(emailForFilter, role);
}

export async function getMission(id: string): Promise<Mission | null> {
  const gw = createMissionsGateway();
  return gw.get(id);
}

export async function createMission(newMission: Omit<Mission, 'id'>): Promise<Mission> {
  guardRouteMutation();
  const gw = createMissionsGateway();
  const created = await gw.create(newMission);
  void logAudit(getActorEmailSafe(), 'mission.create', created.id, { title: created.title });
  return created;
}

export async function updateMission(id: string, updates: Partial<Mission>): Promise<Mission> {
  guardRouteMutation();
  const gw = createMissionsGateway();
  const updated = await gw.update(id, updates);
  void logAudit(getActorEmailSafe(), 'mission.update', id, { updates });
  return updated;
}

export async function archiveMission(id: string): Promise<Mission> {
  const gw = createMissionsGateway();
  const result = await gw.archive(id);
  void logAudit(getActorEmailSafe(), 'mission.archive', id);
  return result;
}

export async function unarchiveMission(id: string): Promise<Mission> {
  const gw = createMissionsGateway();
  const result = await gw.unarchive(id);
  void logAudit(getActorEmailSafe(), 'mission.unarchive', id);
  return result;
}

export async function rateMission(
  id: string,
  rating: { decisionMaker: boolean; demoPerformed: boolean; wonAmountEur: number },
): Promise<Mission> {
  const gw = createMissionsGateway();
  return gw.rate(id, rating);
}

export async function deleteMission(id: string): Promise<void> {
  guardRouteMutation();
  const gw = createMissionsGateway();
  await gw.delete(id);
  void logAudit(getActorEmailSafe(), 'mission.delete', id);
}

// Mission-Lead relationship functions
export async function assignLeadsToMission(missionId: string, leadIds: string[]): Promise<MissionLead[]> {
  guardRouteMutation();
  const gw = createMissionsGateway();
  const created = await gw.assignLeads(missionId, leadIds);
  void logAudit(getActorEmailSafe(), 'mission_leads.assign', missionId, { count: created.length });
  return created;
}

export async function listMissionLeads(
  missionId: string,
  opts?: { signal?: AbortSignal },
): Promise<MissionLead[]> {
  // Stacks PR #1114 (signal pass-through, since merged into staging)
  // with PR #1124's burndown intent: no memoryMissionLeads fallback,
  // no try/catch — SL errors propagate to consumers.
  const gw = createMissionsGateway();
  return gw.listLeads(missionId, opts);
}

export async function reorderMissionLead(
  missionId: string,
  missionLeadId: string,
  newIndex: number,
): Promise<MissionLead[]> {
  guardRouteMutation();
  const gw = createMissionsGateway();
  return gw.reorderLead(missionId, missionLeadId, newIndex);
}

export async function deleteMissionLead(id: string): Promise<void> {
  guardRouteMutation();
  const gw = createMissionsGateway();
  await gw.deleteLead(id);
  void logAudit(getActorEmailSafe(), 'mission_lead.delete', id);
}

export async function updateMissionLead(
  id: string,
  updates: { plannedTime?: string; status?: string; pinned?: boolean; is_anchor?: boolean; anchor_source?: string },
  missionId?: string,
): Promise<MissionLead> {
  guardRouteMutation();
  const gw = createMissionsGateway();
  // Gateway handles `missionId` absence with a fallback URL shape
  // (`/v1/missions/stops/${missionLeadId}`). No client-side missionId
  // resolution from a stale local cache.
  const updated = await gw.updateLead(id, updates, missionId);
  void logAudit(getActorEmailSafe(), 'mission_lead.update', id, { updates });
  return updated;
}
